import { DeskLocationEntity, IFilterResource, ResourceEntity } from "@features/resources/services/types/resourceEntity";
import { create } from "zustand";
import { devtools } from "zustand/middleware";

interface ResourceState {
  // Available and selected resources (master data)
  availableResource: ResourceEntity[];
  selectedResource: ResourceEntity[];
  resources: ResourceEntity[];
  meetingLabel: number | null;
  deskLocationList: DeskLocationEntity[];
  originalSelectedResources: ResourceEntity[] | null;
  filterResource?: IFilterResource;
  isCheckSyncResource: boolean;
  // Resource management methods
  setAvailableResource: (resources: ResourceEntity[]) => void;
  setSelectedResource: (resources: ResourceEntity[], options?: { skipValidation?: boolean }) => void;
  addSelectedResource: (resource: ResourceEntity) => void;
  removeSelectedResource: (resource: ResourceEntity) => void;
  setResources: (resources: ResourceEntity[]) => void;

  // Utility methods
  clearSelectedResources: () => void;
  //reopen
  deleteSelectWhenFromOutlook: (email: string[]) => void;

  // Validation helper - exposed for external use
  validateResourceBeforeAdd: (resource: ResourceEntity) => boolean;
  setMeetingLabel: (meetingLabel: number | null) => void;
  setDeskLocationList: (deskLocationList: DeskLocationEntity[]) => void;
  setOriginalSelectedResources: (originalSelectedResources: ResourceEntity[] | null) => void;
  setFilterResource: (filterResource: IFilterResource | ((prev: IFilterResource | undefined) => IFilterResource)) => void;
  setIsCheckSyncResource: (isCheckSyncResource: boolean) => void;
  updateResourceIsOrdered: (email: string, isOrdered: boolean) => void;
}

const initialState = {
  availableResource: [],
  selectedResource: [],
  resources: [],
  meetingLabel: null,
  deskLocationList: [],
  isCheckSyncResource: true,
};

export const useResourceStore = create<ResourceState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      setAvailableResource: (availableResource) => {
        // console.log("📋 ResourceStore: Setting available resources", availableResource.length);
        set({ availableResource }, false, "setAvailableResource");
      },

      setSelectedResource: (selectedResource, options?: { skipValidation?: boolean }) => {
        console.log("🚀 ~ selectedResource:", selectedResource)
        const validResources = selectedResource.filter((r) => r.email || r.childResources?.length > 0);

        if (validResources.length !== selectedResource.length) {
          console.warn("⚠️ ResourceStore: Filtered out invalid resources", {
            original: selectedResource.length,
            valid: validResources.length,
            invalid: selectedResource.length - validResources.length,
          });
        }

        set({ selectedResource: validResources }, false, "setSelectedResource");
      },

      addSelectedResource: (resource) => {
        // console.log("➕ ResourceStore: Adding selected resource", resource.name, resource.email);
        set(
          (state) => {
            // Enhanced duplicate checking with detailed logging
            const existsByEmail = state.selectedResource.find(
              (r) => r.email.toLowerCase() === resource.email.toLowerCase()
            );
            const existsByName = state.selectedResource.find(
              (r) => r.name.toLowerCase() === resource.name.toLowerCase()
            );

            if (existsByEmail) {
              console.warn("⚠️ DUPLICATE DETECTED: Resource already selected by EMAIL:", {
                incoming: { name: resource.name, email: resource.email },
                existing: { name: existsByEmail.name, email: existsByEmail.email },
              });
              return state;
            }

            if (existsByName) {
              console.warn("⚠️ DUPLICATE DETECTED: Resource already selected by NAME:", {
                incoming: { name: resource.name, email: resource.email },
                existing: { name: existsByName.name, email: existsByName.email },
              });
              return state;
            }

            // Remove from available resources
            const newAvailable = state.availableResource.filter(
              (r) => r.email.toLowerCase() !== resource.email.toLowerCase()
            );
            const newSelected = [resource, ...state.selectedResource];

            // console.log("✅ ResourceStore: Resource added successfully", {
            //   resourceName: resource.name,
            //   newSelectedCount: newSelected.length,
            //   newAvailableCount: newAvailable.length,
            // });

            return {
              availableResource: newAvailable,
              selectedResource: newSelected,
            };
          },
          false,
          "addSelectedResource"
        );
      },

      // Validation helper - checks if resource can be added (basic validation)
      validateResourceBeforeAdd: (resource) => {
        const state = get();
        // Check for duplicates
        const existsByEmail = state.selectedResource.find(
          (r) => r.email.toLowerCase() === resource.email.toLowerCase()
        );
        const existsByName = state.selectedResource.find(
          (r) => r.name.toLowerCase() === resource.name.toLowerCase()
        );

        if (existsByEmail || existsByName) {
          return false;
        }

        // Basic validation - resource must have email and name
        if (!resource.email || !resource.name) {
          return false;
        }

        return true;
      },

      removeSelectedResource: (resource) => {
        // console.log("➖ ResourceStore: Removing selected resource", resource.name);
        set(
          (state) => {
            // Remove from selected resources
            const newSelected = state.selectedResource.filter(
              (r) => r.email.toLowerCase() !== resource.email.toLowerCase()
            );

            // Add back to available resources if not already there
            const exists = state.availableResource.some((r) => r.email.toLowerCase() === resource.email.toLowerCase());
            const newAvailable = exists ? state.availableResource : [...state.availableResource, resource];

            return {
              availableResource: newAvailable,
              selectedResource: newSelected,
            };
          },
          false,
          "removeSelectedResource"
        );
      },

      setResources: (resources) => {
        // console.log("🎯 ResourceStore: Setting resources", resources.length);
        set({ resources }, false, "setResources");
      },

      // Utility methods
      clearSelectedResources: () => {
        // console.log("🧹 ResourceStore: Clearing selected resources");
        set(
          (state) => ({
            // availableResource: [...state.availableResource, ...state.selectedResource],
            // selectedResource: [],
          }),
          false,
          "clearSelectedResources"
        );
      },
      deleteSelectWhenFromOutlook: (email: string[]) => {
        set((state) => {
          const resourceSelected: ResourceEntity[] = [...state.selectedResource];
          const newSelectedResource = resourceSelected.filter((resource) => !email.includes(resource.email));
          return {
            selectedResource: newSelectedResource,
          };
        }, false, "deleteSelectWhenFromOutlook");
      },
      setMeetingLabel: (meetingLabel: number | null) => {
        set({ meetingLabel }, false, "setMeetingLabel");
      },
      setDeskLocationList: (deskLocationList) => {
        set({ deskLocationList }, false, "setDeskLocationList");
      },
      setOriginalSelectedResources: (originalSelectedResources: ResourceEntity[] | null) => {
        set({ originalSelectedResources }, false, "setOriginalSelectedResources");
      },
      setFilterResource: (filterResource: IFilterResource | ((prev: IFilterResource | undefined) => IFilterResource)) => {
        if (typeof filterResource === "function") {
          set((state) => ({ filterResource: filterResource(state.filterResource) }), false, "setFilterResource");
        } else {
          set({ filterResource }, false, "setFilterResource");
        }
      },
      setIsCheckSyncResource: (isCheckSyncResource: boolean) => {
        set({ isCheckSyncResource }, false, "setIsCheckSyncResource");
      },
      updateResourceIsOrdered: (email: string, isOrdered: boolean) => {
        set((state) => {
          const selectedResource = [...state.selectedResource];
          const index = selectedResource.findIndex((resource) => resource.email.toLowerCase() === email.toLowerCase());
          if(index !== -1) {
            selectedResource[index].isOrdered = isOrdered;
          }
          return {
            selectedResource: selectedResource,
          };
        }, false, "updateResourceIsOrdered");
      },
    }),
    {
      name: "resource-store"
    }
  )
);
